import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { getFeedbackPackage } from './feedbackApi.js';
import './feedbackMarkup.redesign.css';

// W3A — the submit confirmation. Submitting feedback ends on reassurance and
// a tracking link (never the triager's queue): reference IDs for every
// governed record created, plus the way back to what the tester was doing.
export function FeedbackSubmittedPage() {
  const { packageId } = useParams();
  const location = useLocation();
  const [fallback, setFallback] = useState(null);
  const [fallbackFailed, setFallbackFailed] = useState(false);

  const fromState = location.state || null;
  const title = fromState?.title || fallback?.title || packageId;
  const recordIds = fromState?.recordIds?.length
    ? fromState.recordIds
    : fallback?.submittedFeedbackIds || [];
  const sourceRoute = fromState?.sourceRoute || fallback?.primaryRoute || '/home';

  useEffect(() => {
    // Deep links (or refreshes) arrive without navigation state — the owner
    // can always recover the reference IDs from the package itself.
    if (fromState?.recordIds?.length) return;
    let cancelled = false;
    getFeedbackPackage(packageId)
      .then((response) => {
        if (!cancelled) setFallback(response?.data || null);
      })
      .catch(() => {
        if (!cancelled) setFallbackFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fromState?.recordIds?.length, packageId]);

  const count = recordIds.length;

  // A deep link to an inaccessible or unknown package must not fabricate a
  // success confirmation with the raw id as the title.
  if (fallbackFailed && !fromState?.recordIds?.length && !fallback) {
    return (
      <section className="feedback-submitted-page fbmk-redesign" data-testid="feedback-submitted">
        <div className="feedback-submitted-card">
          <h1 data-testid="feedback-submitted-heading">This confirmation is no longer available</h1>
          <p>
            The submission receipt could not be loaded — the package may have been removed, or it
            may belong to another account. Your feedback history is always available under{' '}
            <strong>My feedback</strong>.
          </p>
          <div className="feedback-submitted-actions">
            <Link className="btn primary" data-testid="feedback-submitted-track" to="/feedback?view=submitted">
              Open My feedback
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="feedback-submitted-page fbmk-redesign" data-testid="feedback-submitted">
      <div className="feedback-submitted-card">
        <div className="feedback-submitted-check" aria-hidden>
          ✓
        </div>
        <h1 data-testid="feedback-submitted-heading">
          {count
            ? `We received your ${count} request${count === 1 ? '' : 's'}`
            : 'We received your feedback'}
        </h1>
        <p>
          “{title}” is with the PRIME product team, screenshots and all. You will see status changes
          and replies under <strong>My feedback</strong>.
        </p>
        {recordIds.length ? (
          <ul className="feedback-submitted-refs" data-testid="feedback-submitted-refs">
            {recordIds.map((recordId) => (
              <li key={recordId}>
                <code>{recordId}</code>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="feedback-submitted-actions">
          <Link
            className="btn primary"
            data-testid="feedback-submitted-view"
            to={`/feedback/${encodeURIComponent(packageId)}`}
          >
            View submitted feedback
          </Link>
          <Link
            className="btn ghost"
            data-testid="feedback-submitted-track"
            to="/feedback?view=submitted"
            state={{ highlightSubmitted: true }}
          >
            Track in My feedback
          </Link>
          <Link className="btn ghost" data-testid="feedback-submitted-back" to={sourceRoute}>
            Back to what I was doing
          </Link>
        </div>
        <p className="feedback-submitted-note">
          You can keep testing — feedback never interrupts your sandbox work.
        </p>
      </div>
    </section>
  );
}
